import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  CheckSquare,
  Copy,
  Download,
  FileText,
  History,
  MoreHorizontal,
  Share2,
  Sparkles,
  Trash2,
  UserPlus,
  Users,
  Wand2,
} from 'lucide-react'
import {
  AvatarGroup,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dropdown,
  IconButton,
  Skeleton,
  Tabs,
} from '@/components/ui'
import {
  ConfirmDialog,
  MeetingStatusBadge,
  PageHeader,
  PlatformIcon,
  QueryBoundary,
} from '@/components/domain'

import {
  formatDayLabel,
  formatDuration,
  formatPercent,
  formatPlatformMeetingId,
  formatTime,
} from '@/internationalization/format'
import { PLATFORM_LABEL } from '@/internationalization/constants'
import { useT } from '@/internationalization/i18n'
import { PERMISSIONS, usePermissions } from '@/features/authorization'

import { toast } from '@/stores'
import { ApiError } from '@/internationalization/api-client'
import { useDisclosure } from '@/hooks'
import { useMutation } from '@tanstack/react-query'
import { meetingsApi } from './api'
import { TranscriptPanel } from '@/features/transcript/TranscriptPanel'
import { SummaryPanel } from '@/features/summary/SummaryPanel'
import { ActionItemList } from '@/features/actions/components/ActionItemList'
import { AddActionItem } from '@/features/actions/components/AddActionItem'
import { MeetingPatrons } from '@/features/patrons'
import { MeetingPlayer, MeetingPlayerBar } from '@/features/player'
import { useMeeting } from './hooks'

type TabId = 'summary' | 'transcript' | 'clean' | 'actions' | 'participants'

// Flip to true to bring the "Cleaned" tab back. Hidden for now; the backend and
// stored clean transcripts are untouched.
const SHOW_CLEANED_TAB = false

const TAB_IDS: TabId[] = [
  'summary',
  'transcript',
  ...(SHOW_CLEANED_TAB ? (['clean'] as TabId[]) : []),
  'actions',
  'participants',
]

function isTabId(value: string | null): value is TabId {
  return value !== null && (TAB_IDS as string[]).includes(value)
}

/**
 * One meeting, five tabs.
 *
 * Summary is first because it is what people open the page for; the transcript
 * is the evidence they check it against. The tab is seeded from a `?tab=`
 * search param rather than a nested route, so the page does not remount and
 * lose the player position — the one thing a nested route would have cost.
 * `setTab` still only touches component state on a later click; the URL is
 * re-applied whenever `meetingId` or `?tab=` changes, as a deep-link entry
 * point (e.g. a notification linking straight to a meeting's Actions tab).
 * That re-apply has to be an effect rather than a `useState` initializer:
 * navigating from one meeting to another reuses this component instance, so
 * an initializer would only ever see the very first meeting opened.
 */
export function MeetingDetailPage() {
  const t = useT()
  const { meetingId } = useParams<{ meetingId: string }>()
  const { data: meeting, isPending, error, refetch } = useMeeting(meetingId)
  const [searchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const highlightActionItemId = searchParams.get('actionItemId') ?? undefined
  const [tab, setTab] = useState<TabId>(isTabId(requestedTab) ? requestedTab : 'summary')

  // The component instance is reused across meetings (same route, different
  // :meetingId), so a useState initializer alone only seeds the tab on the
  // very first mount. Re-apply the requested tab whenever the meeting or the
  // deep-link params change, so a notification link to a different meeting's
  // Actions tab actually switches there instead of leaving the previous
  // meeting's tab selected.
  //
  // `actionItemId` is a dependency too: a link to a second item in the same
  // meeting has the same `meetingId` and `?tab=actions`, so without it the
  // page stayed on whichever tab had been clicked since. `deepLinkKey` covers
  // re-clicking the SAME item's link after leaving the tab -- the URL is
  // identical, but every navigation mints a new `location.key`. It is only
  // consulted when an action item is named, so a plain `?tab=` link or any
  // unrelated navigation never resets what the reader is looking at.
  const location = useLocation()
  const deepLinkKey = highlightActionItemId ? location.key : undefined
  useEffect(() => {
    setTab(isTabId(requestedTab) ? requestedTab : 'summary')
    setTranscriptFocus(null)
  }, [meetingId, requestedTab, highlightActionItemId, deepLinkKey])

  // A timecode on Summary/Actions that cannot play (no audio, or a role without
  // `audio:download`) opens the transcript at that moment instead, so somebody
  // who can read the meeting can still reach the line a decision or action item
  // cites. Cleared on any manual tab change so it seeds only the visit it was
  // asked for.
  const [transcriptFocus, setTranscriptFocus] = useState<number | null>(null)
  const openTranscriptAt = useCallback((seconds: number) => {
    setTranscriptFocus(seconds)
    setTab('transcript')
  }, [])
  const deleteDialog = useDisclosure()
  const navigate = useNavigate()
  const { has } = usePermissions()
  const canDelete = has(PERMISSIONS.meetingDelete)

  // The share link is a bearer credential, so the toast says what it grants
  // rather than just confirming the copy.
  const share = useMutation({
    mutationFn: () => meetingsApi.share(meetingId as string),
    onSuccess: async (link) => {
      await navigator.clipboard.writeText(link.url).catch(() => undefined)
      toast.success(t('meetingDetail.toast.shareLinkCopied'), t('meetingDetail.toast.shareLinkCopiedBody'))
    },
    onError: () => toast.error(t('meetingDetail.toast.couldNotCreateShareLink')),
  })

  
 const remove = useMutation({
  mutationFn: () => meetingsApi.remove(meetingId as string),

  onSuccess: () => {
    toast.success(
      t('meetingDetail.toast.meetingDeleted'),
      t('meetingDetail.toast.meetingDeletedBody')
    )
    navigate('/meetings', { replace: true })
  },

  onError: () => toast.error(t('meetingDetail.toast.deleteFailed')),
})

  // TEMPORARY: re-run ASR repair over a meeting that is already transcribed,
  // so the cleaning can be judged on real recordings. It touches nothing a
  // reader is looking at — no re-transcription, no new summary — and the toast
  // says so, because "clean" next to "Re-run transcription" in the same UI
  // would otherwise read as the same kind of destructive act.
  const clean = useMutation({
    mutationFn: () => meetingsApi.clean(meetingId as string),
    onSuccess: () =>
      toast.info(
        t('meetingDetail.toast.cleaningStarted'),
        t('meetingDetail.toast.cleaningStartedBody'),
      ),
    onError: (err) =>
      toast.error(
        err instanceof ApiError && err.code === 'clean_unavailable'
          ? t('meetingDetail.toast.cleaningNotEnabled')
          : t('meetingDetail.toast.couldNotStartCleaning'),
      ),
  })

  /**
   * Ask the recorder to knock again on a meeting whose host refused it.
   *
   * WHY THE PRODUCT HAS TO OFFER THIS AT ALL. Teams has no un-deny, and
   * re-inviting the bot does nothing: it is the same meeting, so the launch
   * claim taken before the recorder started still stands and every scheduler
   * tick says "already launched". Until this existed the recovery was an
   * engineer deleting a row by hand.
   *
   * THE SUCCESS TOAST SAYS WHAT WILL HAPPEN, NOT THAT IT HAS. Nothing has
   * joined anything yet -- the claim is dropped and the scheduler launches on
   * its next sub-tick, with the bot in the lobby about a minute later, where
   * somebody still has to admit it. "Done" would send them looking for a bot
   * that is not there.
   *
   * The refusals are named individually for the same reason the API names
   * them: "already on its way", "asked too many times" and "the meeting is
   * over" are three different things to do next, and a generic failure is
   * none of them.
   */
  const rejoin = useMutation({
    mutationFn: () => meetingsApi.rejoin(meetingId as string),
    onSuccess: async () => {
      toast.success(
        t('meetingDetail.toast.rejoinRequested'),
        t('meetingDetail.toast.rejoinRequestedBody'),
      )
      // `canRejoin` is now false server-side, and refetching is what takes the
      // button away -- leaving it live invites the second press this endpoint
      // refuses, which reads as the first one not having worked.
      await refetch()
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : undefined
      if (code === 'rejoin_already_requested') {
        toast.info(t('meetingDetail.toast.rejoinAlreadyRequested'))
      } else if (code === 'rejoin_limit_reached') {
        toast.info(t('meetingDetail.toast.rejoinLimitReached'))
      } else if (code === 'meeting_over') {
        toast.info(t('meetingDetail.toast.rejoinMeetingOver'))
      } else {
        toast.error(t('meetingDetail.toast.rejoinFailed'))
      }
      // Every refusal above is a fact about the meeting that this page is now
      // out of date about, so it re-reads rather than leaving a button that
      // will be refused again.
      void refetch()
    },
  })

  /**
   * Download the Meeting Outcome Report PDF -- metadata, the existing
   * summary, decisions and action items, built server-side from the same
   * data this page already renders. Never the transcript (speech-recognition
   * errors have no place in an official report) or the participant list (the
   * Participants tab already has that).
   *
   * A BLOB DOWNLOAD RATHER THAN A LINK, because the access token lives in
   * memory and travels as a header -- an `<a href>` straight at the API
   * would be sent unauthenticated. The object URL is revoked right after the
   * click so nothing keeps the PDF's bytes alive in memory longer than the
   * download needs.
   */
  const exportPdf = useMutation({
    mutationFn: () => meetingsApi.exportPdf(meetingId as string),
    onSuccess: (blob) => {
      const slug = (meeting?.title ?? 'meeting').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${slug || 'meeting'}-outcome-report.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error(t('meetingDetail.toast.exportFailed')),
  })

  return (
    <div>
      <QueryBoundary
        isLoading={isPending}
        error={error}
        onRetry={refetch}
        skeleton={
          <div className="space-y-4">
            <Skeleton className="h-6 w-64" />
            <Skeleton shape="block" className="h-24 w-full" />
            <Skeleton shape="block" className="h-80 w-full" />
          </div>
        }
      >
        {meeting && (
          /*
            THE PROVIDER WRAPS THE WHOLE PAGE, the bar renders under the
            header. An `<audio>` element inside a tab is unmounted the moment
            somebody switches to the summary, which stops playback and loses
            the position — so the element has to live above the tabs even
            though the controls belong below them.

            `enabled` is what keeps a scheduled meeting from asking: the
            playable file is written by the TRANSCRIPTION stage, so a meeting
            with no recording and no transcript cannot have one, and a request
            per page view for an object that is definitely absent is a HEAD
            nobody needed. Everything else asks and renders whatever answer it
            gets, including "nothing stored yet".
          */
          <MeetingPlayer
            meetingId={meeting.id}
            enabled={meeting.hasRecording || meeting.segments.length > 0}
          >
            <PageHeader
              eyebrow={
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <Link
                    to="/meetings"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-fg-muted hover:text-fg"
                  >
                    <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
                    {t('meetingDetail.allMeetings')}
                  </Link>
                  {meeting.isRecurring && meeting.seriesId && (
                    <Link
                      to={`/meetings/series/${encodeURIComponent(meeting.seriesId)}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-fg-muted hover:text-fg"
                    >
                      <History aria-hidden className="h-3.5 w-3.5" />
                      {t('meetingDetail.backToSeries')}
                    </Link>
                  )}
                </div>
              }
              title={meeting.title}
              description={
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-flex items-center gap-1.5">
                    <PlatformIcon platform={meeting.platform} size="sm" />
                    {PLATFORM_LABEL[meeting.platform]}
                  </span>
                  <span>
                    {formatDayLabel(meeting.startsAt)} · {formatTime(meeting.startsAt)}–
                    {formatTime(meeting.endsAt)}
                  </span>
                  <span>{t('meetingDetail.recorded', { duration: formatDuration(meeting.durationSeconds) })}</span>
                  {/*
                    The id printed on the invite, so somebody can check this is
                    the call they meant and search by it later. Copies the
                    unspaced form, which is what search matches exactly.
                  */}
                  {meeting.platformMeetingId && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-mono text-xs hover:text-fg"
                      title={t('meetingDetail.copyMeetingId')}
                      aria-label={t('meetingDetail.copyMeetingId')}
                      onClick={() => {
                        const id = meeting.platformMeetingId as string
                        void navigator.clipboard
                          .writeText(id)
                          .then(() => toast.success(t('meetingDetail.toast.meetingIdCopied')))
                          .catch(() => undefined)
                      }}
                    >
                      {t('meetingDetail.meetingId', { id: formatPlatformMeetingId(meeting.platformMeetingId) })}
                      <Copy aria-hidden className="h-3 w-3" />
                    </button>
                  )}
                  <MeetingStatusBadge status={meeting.status} leaveReason={meeting.leaveReason} />
                </span>
              }
              actions={
                <>
                  {/*
                    FIRST, AND ONLY WHEN THE SERVER SAYS SO. It is the one
                    action on this page with a deadline: it re-arms a meeting
                    that is still running, and `canRejoin` goes false the
                    moment that meeting ends. `canRejoin` is the server's
                    answer rather than a check on `leaveReason` here, because
                    two of its clauses -- a request already in flight, and the
                    per-meeting cap -- are not in this payload at all.
                  */}
                  {meeting.canRejoin && (
                    <Button
                      variant="secondary"
                      leftIcon={<UserPlus className="h-4 w-4" />}
                      isLoading={rejoin.isPending}
                      title={t('meetingDetail.rejoinHint')}
                      onClick={() => rejoin.mutate()}
                    >
                      {t('meetingDetail.rejoin')}
                    </Button>
                  )}
                  <Button
                    leftIcon={<Share2 className="h-4 w-4" />}
                    isLoading={share.isPending}
                    onClick={() => share.mutate()}
                  >
                    {t('meetingDetail.share')}
                  </Button>
                  <Button
                    variant="primary"
                    leftIcon={<Download className="h-4 w-4" />}
                    isLoading={exportPdf.isPending}
                    onClick={() => exportPdf.mutate()}
                  >
                    {t('common.export')}
                  </Button>
                  <Dropdown
                    trigger={({ toggle }) => (
                      <IconButton label={t('meetingDetail.moreActions')} icon={<MoreHorizontal />} variant="secondary" onClick={toggle} />
                    )}
                   items={[
  {
    id: 'resend',
    label: t('meetingDetail.reSendSummaryEmail'),
    onSelect: () => toast.info(t('meetingDetail.toast.summaryEmailQueued')),
  },
  ...(canDelete
    ? [
        {
          id: 'delete',
          label: t('meetingDetail.deleteMeetingAndAudio'),
          icon: <Trash2 />,
          destructive: true,
          separatorBefore: true,
          onSelect: deleteDialog.open,
        },
      ]
    : []),
]}
                  />
                </>
              }
              below={
                <div className="space-y-3">
                  <MeetingPlayerBar />
                  {/* Above the tabs rather than inside one: which patron a
                      meeting is about is context for reading everything below
                      it, and it is also the only place an INTERNAL discussion
                      about a patron can be recorded at all. */}
                  <MeetingPatrons meetingId={meeting.id} patrons={meeting.patrons} />
                  <Tabs
                  activeId={tab}
                  onChange={(id) => {
                    setTranscriptFocus(null)
                    setTab(id as TabId)
                  }}
                  tabs={[
                    { id: 'summary', label: t('meetingDetail.tab.summary'), icon: <Sparkles /> },
                    { id: 'transcript', label: t('meetingDetail.tab.transcript'), icon: <FileText /> },
                    /* A TAB RATHER THAN A TOGGLE INSIDE THE TRANSCRIPT. The two
                       are different documents with different standing — one is
                       the record of what was said, the other is a model's
                       reading of it — and a toggle would let somebody quote the
                       repaired wording believing they were reading the
                       recording. The count is the number of lines that differ,
                       so a tab showing "Cleaned 0" is a transcript the repair
                       found nothing wrong with, and no badge at all means the
                       repair has never run here. */
                    ...(SHOW_CLEANED_TAB
                      ? [
                          {
                            id: 'clean',
                            label: t('meetingDetail.tab.cleaned'),
                            icon: <Wand2 />,
                            count: meeting.cleanTranscript?.edits,
                          },
                        ]
                      : []),

                    { id: 'actions', label: t('meetingDetail.tab.actions'), icon: <CheckSquare />, count: meeting.actionItems.length },
                    { id: 'participants', label: t('meetingDetail.tab.participants'), icon: <Users />, count: meeting.participants.length },
                  ]}
                  />
                </div>
              }
            />
            {tab === 'summary' && (
              <SummaryPanel
                meeting={meeting}
                onOpenTranscriptAt={meeting.segments.length > 0 ? openTranscriptAt : undefined}
              />
            )}
            {tab === 'transcript' && (
              <TranscriptPanel meeting={meeting} initialSeconds={transcriptFocus ?? undefined} />
            )}
            {tab === 'clean' && (
              <TranscriptPanel
                meeting={meeting}
                variant="clean"
                onClean={() => clean.mutate()}
                isCleaning={clean.isPending}
              />
            )}
            {tab === 'actions' && (
              <div className="space-y-3">
                <ActionItemList
                  items={meeting.actionItems}
                  context="meeting"
                  highlightId={highlightActionItemId}
                  onOpenTranscriptAt={meeting.segments.length > 0 ? openTranscriptAt : undefined}
                />
                {/* Adding one needs a meeting, so it lives here rather than on
                    the workspace-wide list: there is no roster to assign from
                    and nobody to show it to without one. */}
                <AddActionItem meetingId={meeting.id} />
              </div>
            )}
            {tab === 'participants' && (
              <Card>
                <CardHeader
                  title={t('meetingDetail.participantsTitle')}
                  description={t('meetingDetail.participantsDesc')}
                  action={<AvatarGroup people={meeting.participants} max={6} />}
                />
                <CardBody>
                  <ul className="divide-y divide-border">
                    {meeting.participants.map((person) => (
                      <li key={person.id} className="flex items-center gap-3 py-2.5">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-base text-fg">{person.displayName}</span>
                          <span className="block truncate text-sm text-fg-subtle">
                            {person.email ?? t('meetingDetail.noEmailCaptured')}
                          </span>
                        </span>
                        {person.isOrganizer && <Badge tone="brand">{t('meetingDetail.organiser')}</Badge>}
                        {person.isExternal && <Badge tone="warning">{t('meetingDetail.external')}</Badge>}
                        <span className="w-16 shrink-0 text-right font-mono text-sm tabular-nums text-fg-muted">
                          {person.talkRatio === null ? '—' : formatPercent(person.talkRatio)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            )}
            <ConfirmDialog
              isOpen={deleteDialog.isOpen}
              onCancel={deleteDialog.close}
              onConfirm={() => {
                deleteDialog.close()

                remove.mutate()
              }}

              title={t('meetingDetail.deleteTitle')}
              description={t('meetingDetail.deleteBody')}
              confirmLabel={t('meetingDetail.deletePermanently')}
              isDestructive
              isLoading={remove.isPending}
            />
          </MeetingPlayer>
        )}
      </QueryBoundary>
    </div>
  )
}
